import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type {
  Ampel, Schreibbild, SchreibbildFeld, SchreibbildVariante, VorschauArtikel,
} from '../lib/typen';
import { euro, zahl } from '../lib/format';
import { fehlendeAttributwerte, masse, spaltenBedarf } from '../lib/schreibbild';

/**
 * Ein Artikel je Zeile, aufklappbar zu seinen Varianten.
 *
 * Drei Ampeln je Zeile, weil sie Verschiedenes bedeuten:
 *
 *   **Existenz**   gruen = wird angelegt · gelb = unklar · rot = existiert bereits,
 *                  wird uebersprungen
 *   **Kategorie**  gruen = Produktart zugeordnet · gelb = unsicher oder unvollstaendig ·
 *                  rot = keine Kategorie. Rot heisst hier NICHT „blockiert": der Artikel
 *                  wird trotzdem angelegt, nur ohne Kategorie (Entscheidung 2.4).
 *   **Bild**       gruen = jede Farbe hat ein Foto · gelb = ein Teil · rot = keines.
 *                  Auch das blockiert nicht — der Artikel entsteht ohne Bild und wird
 *                  markiert. Ist die Ampel GRAU, war gar kein Bildordner gewaehlt:
 *                  dann wurde nicht nachgesehen, und das ist etwas anderes als rot.
 *
 * Der Hinweistext steht dabei, nicht nur die Farbe — eine Ampel ohne Begruendung
 * zwingt zum Raten.
 */
export default function ArtikelListe({ artikel }: { artikel: VorschauArtikel[] }) {
  const [offen, setOffen] = useState<Set<string>>(new Set());

  if (artikel.length === 0) {
    return (
      <p className="text-sm text-[#1A1A1A]/60">
        Keine Artikel. Entweder ist die Datei leer, oder alle Zeilen sind durch die
        Filter des Profils gefallen.
      </p>
    );
  }

  function umschalten(schluessel: string) {
    setOffen(vorher => {
      const nachher = new Set(vorher);
      if (nachher.has(schluessel)) nachher.delete(schluessel);
      else nachher.add(schluessel);
      return nachher;
    });
  }

  return (
    <div className="divide-y divide-[#DAD9D9] border-y border-[#DAD9D9]">
      {artikel.map(a => {
        const istOffen = offen.has(a.schluessel);
        const hinweise = [...a.existenz.hinweise, ...a.kategorien.hinweise, ...a.hinweise];
        return (
          <div key={a.schluessel}>
            <button
              type="button"
              onClick={() => umschalten(a.schluessel)}
              className="flex w-full items-start gap-3 px-2 py-3 text-left hover:bg-[#F7F7F5]"
            >
              {istOffen
                ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-[#1A1A1A]/50" />
                : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-[#1A1A1A]/50" />}

              <span className="mt-1.5 flex shrink-0 gap-1" title="Existenz · Kategorie · Bild">
                <AmpelPunkt ampel={a.existenz.ampel} />
                <AmpelPunkt ampel={a.kategorien.ampel} />
                <AmpelPunkt ampel={a.bilder.ampel} />
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-medium text-[#1A1A1A]">{a.name}</span>
                  <span className="text-sm text-[#1A1A1A]/60">
                    {zahl(a.varianten)} {a.varianten === 1 ? 'Variante' : 'Varianten'}
                  </span>
                  <StatusSchild artikel={a} />
                  <KategorieSchild artikel={a} />
                  <BildSchild artikel={a} />
                </div>
                {hinweise.length > 0 && (
                  <p className="mt-1 text-xs text-[#1A1A1A]/70">{hinweise.join(' · ')}</p>
                )}
              </div>
            </button>

            {istOffen && <VariantenTabelle artikel={a} />}
          </div>
        );
      })}
    </div>
  );
}

/** `null` = nicht geprueft. Grau statt rot: „nicht nachgesehen" ist kein Befund. */
function AmpelPunkt({ ampel }: { ampel: Ampel | null }) {
  const farbe = ampel === 'gruen' ? 'bg-green-600'
    : ampel === 'gelb' ? 'bg-amber-500'
    : ampel === 'rot' ? 'bg-red-600'
    : 'bg-[#DAD9D9]';
  return <span className={`h-2.5 w-2.5 rounded-full ${farbe}`} />;
}

/**
 * Was mit den Bildern dieses Artikels ist.
 *
 * Ohne gewaehlten Ordner steht hier gar nichts — eine Aussage „kein Bild" waere
 * falsch, wenn niemand nachgesehen hat.
 */
function BildSchild({ artikel }: { artikel: VorschauArtikel }) {
  const { anzahl, ohneBild, ampel, beispiele } = artikel.bilder;
  if (ampel == null) return null;

  const text = anzahl === 0
    ? 'ohne Bild'
    : ohneBild.length > 0
      ? `${zahl(anzahl)} Bilder, ${zahl(ohneBild.length)} Farben ohne`
      : `${zahl(anzahl)} ${anzahl === 1 ? 'Bild' : 'Bilder'}`;

  const stil = ampel === 'gruen' ? 'bg-[#F7F7F5] text-[#1A1A1A]/70'
    : ampel === 'gelb' ? 'bg-amber-50 text-amber-900'
    : 'bg-amber-50 text-amber-900';

  return (
    <span
      className={`rounded px-2 py-0.5 text-xs ${stil}`}
      title={beispiele.length > 0
        ? beispiele.join('\n')
        : 'Der Artikel wird trotzdem angelegt. „Bilder nachliefern" holt sie später nach.'}
    >
      {text}
    </span>
  );
}

function StatusSchild({ artikel }: { artikel: VorschauArtikel }) {
  const { status } = artikel.existenz;
  const text = status === 'existiert' ? 'existiert bereits'
    : status === 'ungeprueft' ? 'nicht prüfbar'
    : 'wird angelegt';
  const stil = status === 'existiert' ? 'bg-red-50 text-red-900'
    : status === 'ungeprueft' ? 'bg-amber-50 text-amber-900'
    : 'bg-green-50 text-green-900';

  return (
    <span className="flex gap-2">
      <span className={`rounded px-2 py-0.5 text-xs ${stil}`}>{text}</span>
      {status !== 'existiert' && (
        <span
          className="rounded bg-[#F7F7F5] px-2 py-0.5 text-xs text-[#1A1A1A]/70"
          title={
            'Feste Regel: die Hauptvariante ist nur aktiv, wenn es keine Untervarianten gibt.'
          }
        >
          Hauptvariante {artikel.hauptvarianteAktiv ? 'aktiv' : 'inaktiv'}
        </span>
      )}
    </span>
  );
}

function KategorieSchild({ artikel }: { artikel: VorschauArtikel }) {
  const { ids, pfade } = artikel.kategorien;

  if (ids.length === 0) {
    return (
      <span className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-900">
        ohne Kategorie
      </span>
    );
  }

  return (
    <span
      className="rounded bg-[#F7F7F5] px-2 py-0.5 text-xs text-[#1A1A1A]/70"
      title={pfade.map(p => `${p.pfad} (${p.id})`).join('\n')}
    >
      {pfade.map(p => p.pfad.split(' > ').slice(-1)[0]).join(' · ')}
    </span>
  );
}


/**
 * Der aufgeklappte Teil: erst im Klartext, was geschrieben wird, dann jede Variante.
 *
 * 🔴 „Hier muss alles angezeigt werden was auch geschrieben wird" (Matze, 21.09.2026).
 * Vorher standen hier Farbe, Größe, EAN und die Preise — Zolltarifnummer, Gewicht,
 * Maße, Attributauflösung, Eigenschaften, Tags und die Bildzuordnung gingen zwar mit
 * nach Plenty, waren aber nirgends zu sehen.
 */
function VariantenTabelle({ artikel }: { artikel: VorschauArtikel }) {
  const bild = artikel.schreibbild;

  return (
    <div className="overflow-x-auto bg-[#F7F7F5] px-2 pb-4">
      {bild
        ? <SchreibBlock bild={bild} />
        : (
          <p className="mb-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
            Was genau geschrieben würde, lässt sich gerade nicht sagen — Plenty war beim
            Aufbau der Vorschau nicht erreichbar. Die Angaben unten kommen aus der Datei.
          </p>
        )}

      {artikel.bilder.ohneBild.length > 0 && (
        <p className="mb-3 text-xs text-amber-800">
          <strong>Ohne Bild bleiben:</strong>{' '}
          {artikel.bilder.ohneBild.slice(0, 8).join(', ')}
          {artikel.bilder.ohneBild.length > 8
            && ` … und ${artikel.bilder.ohneBild.length - 8} weitere`}
          {' '}— die Varianten entstehen trotzdem.
        </p>
      )}

      {artikel.existenz.treffer.length > 0 && (
        <div className="mb-3 rounded border border-red-200 bg-white p-2 text-xs text-[#1A1A1A]/80">
          <strong>Im Bestand gefunden:</strong>{' '}
          {artikel.existenz.treffer.slice(0, 5).map(t => (
            <span key={`${t.ean}-${t.variationId}`} className="mr-3 inline-block">
              EAN {t.ean} → Artikel {t.itemId}, Variante {t.variationId}
              {!t.isActive && ' (inaktiv)'}
              {t.quelle === 'live' && ' (live gefunden)'}
            </span>
          ))}
          {artikel.existenz.treffer.length > 5
            && `… und ${artikel.existenz.treffer.length - 5} weitere`}
        </div>
      )}

      {bild
        ? <SchreibTabelle artikel={artikel} bild={bild} />
        : <DateiTabelle artikel={artikel} />}
    </div>
  );
}

/** Die drei Klartext-Blöcke: der Artikel, was bei jeder Variante gleich ist, was danach folgt. */
function SchreibBlock({ bild }: { bild: Schreibbild }) {
  const [textOffen, setTextOffen] = useState(false);

  return (
    <div className="mb-3 rounded border border-[#DAD9D9] bg-white p-3">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#1A1A1A]/50">
        Was in Plenty geschrieben wird
      </p>

      <div className="grid gap-x-8 gap-y-3 sm:grid-cols-3">
        <Feldgruppe titel="Artikel" felder={bild.artikel} />
        <Feldgruppe titel="Bei jeder Variante" felder={bild.jeVariante} />
        <Feldgruppe titel="Danach" felder={bild.danach} />
      </div>

      {bild.beschreibung && (
        <div className="mt-3 border-t border-[#DAD9D9] pt-2">
          <button
            type="button"
            onClick={() => setTextOffen(o => !o)}
            className="text-xs text-[#1A1A1A]/70 underline hover:text-[#1A1A1A]"
          >
            {textOffen ? 'Beschreibung zuklappen' : 'Beschreibung ansehen'}
          </button>
          {textOffen && (
            <div
              className="mt-2 max-h-60 overflow-y-auto rounded bg-[#F7F7F5] p-2 text-xs text-[#1A1A1A]/80 [&_li]:ml-4 [&_li]:list-disc"
              // Der Text geht genau so nach Plenty. Ihn als HTML zu zeigen ist der
              // einzige Weg, die doppelten Aufzählungspunkte zu sehen, um die es geht.
              dangerouslySetInnerHTML={{ __html: bild.beschreibung }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function Feldgruppe({ titel, felder }: { titel: string; felder: SchreibbildFeld[] }) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-[#1A1A1A]/70">{titel}</p>
      <dl className="space-y-1">
        {felder.map(f => (
          <div key={f.feld} className="text-xs leading-snug">
            <dt className="inline text-[#1A1A1A]/50">{f.feld}: </dt>
            <dd
              className={`inline ${f.weggelassen ? 'text-amber-800' : 'text-[#1A1A1A]'}`}
              title={f.hinweis}
            >
              {f.wert}
              {f.hinweis && <span className="ml-1 text-[#1A1A1A]/40">ⓘ</span>}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Die Variantentabelle aus dem Schreibbild.
 *
 * Zusatzspalten erscheinen nur, wenn der Artikel dort überhaupt Werte hat — eine
 * durchgehend leere Spalte ist keine Auskunft. Dass ein Feld leer bleibt, steht im
 * Block darüber.
 */
function SchreibTabelle({ artikel, bild }: { artikel: VorschauArtikel; bild: Schreibbild }) {
  const bedarf = spaltenBedarf(bild.varianten);
  const fehlend = fehlendeAttributwerte(bild.varianten);
  const preisarten = artikel.zeilen.length > 0 ? Object.keys(artikel.zeilen[0].preise) : [];
  const zeileJeIndex = artikel.zeilen;

  return (
    <>
      {fehlend.length > 0 && (
        <div className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-900">
          <strong>Diese Werte gibt es in Plenty nicht</strong> — die betroffenen Varianten
          bekommen das Attribut nicht, und wenn dadurch zwei Varianten gleich aussehen,
          legt Plenty den Artikel gar nicht erst vollständig an:{' '}
          {fehlend.map(f => (
            <span key={`${f.attributId}-${f.wert}`} className="mr-3 inline-block">
              {f.rolle === 'farbe' ? 'Farbe' : 'Größe'} „{f.wert}" ({zahl(f.anzahl)}×)
            </span>
          ))}
        </div>
      )}

      <table className="w-full min-w-[900px] border-collapse text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-[#1A1A1A]/50">
            <th className="py-1 pr-3 font-medium">Zeile</th>
            <th className="py-1 pr-3 font-medium" title="Geht als externalId nach Plenty">
              externalId
            </th>
            {bedarf.nummer && <th className="py-1 pr-3 font-medium">Nummer</th>}
            <th className="py-1 pr-3 font-medium">Farbe</th>
            <th className="py-1 pr-3 font-medium">Größe</th>
            <th className="py-1 pr-3 font-medium">Barcode</th>
            <th className="py-1 pr-3 text-right font-medium" title="Steht in der Datei, geht NICHT nach Plenty">
              Datei-EK
            </th>
            <th className="py-1 pr-3 text-right font-medium" title="purchasePrice">EK</th>
            {preisarten.filter(a => a !== 'EK').map(art => (
              <th key={art} className="py-1 pr-3 text-right font-medium">{art}</th>
            ))}
            {bedarf.zoll && <th className="py-1 pr-3 font-medium">Zolltarif</th>}
            {bedarf.gewicht && <th className="py-1 pr-3 text-right font-medium">Gewicht</th>}
            {bedarf.masse && <th className="py-1 pr-3 font-medium">Maße (mm)</th>}
            {bedarf.eigenschaften && <th className="py-1 pr-3 font-medium">Eigenschaften</th>}
            {bedarf.tags && <th className="py-1 pr-3 font-medium">Tags</th>}
            {bedarf.bild && <th className="py-1 pr-3 font-medium">Bild</th>}
          </tr>
        </thead>
        <tbody>
          {bild.varianten.map((v, i) => {
            const zeile = zeileJeIndex[i];
            const preisJeArt = new Map(v.preise.map(p => [p.preisart, p.preis]));
            return (
              <tr key={`${v.quellZeile}-${i}`} className={v.istHaupt ? 'font-medium' : ''}>
                <td className="py-1 pr-3 tabular-nums text-[#1A1A1A]/60">
                  {v.quellZeile}
                  {v.istHaupt && (
                    <span className="ml-1 text-[#1A1A1A]/40" title="Hauptvariante">★</span>
                  )}
                </td>
                <td className="py-1 pr-3 tabular-nums">{v.externalId || '–'}</td>
                {bedarf.nummer && <td className="py-1 pr-3 tabular-nums">{v.nummer || '–'}</td>}
                <Attributzelle variante={v} rolle="farbe" />
                <Attributzelle variante={v} rolle="groesse" />
                <td className="py-1 pr-3 tabular-nums">
                  {v.barcode ?? (v.barcodeWeggelassen
                    ? (
                      <span
                        className="text-amber-800 line-through"
                        title="Die Hauptvariante bekommt bei mehreren Varianten keinen Barcode — wie im Bestand."
                      >
                        {v.barcodeWeggelassen}
                      </span>
                    )
                    : <span className="text-red-700">fehlt</span>)}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums text-[#1A1A1A]/40">
                  {zeile ? euro(zeile.importEk) : '–'}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">
                  {v.einkaufspreis == null
                    ? <span className="text-[#1A1A1A]/40">–</span>
                    : euro(v.einkaufspreis)}
                </td>
                {preisarten.filter(a => a !== 'EK').map(art => (
                  <td key={art} className="py-1 pr-3 text-right tabular-nums">
                    {preisJeArt.has(art)
                      ? euro(preisJeArt.get(art) as number)
                      : (
                        <span className="text-[#1A1A1A]/40" title="Wird nicht geschrieben">
                          –
                        </span>
                      )}
                  </td>
                ))}
                {bedarf.zoll && (
                  <td className="py-1 pr-3 tabular-nums">{v.zolltarifnummer || '–'}</td>
                )}
                {bedarf.gewicht && (
                  <td className="py-1 pr-3 text-right tabular-nums">
                    {v.gewichtG == null ? '–' : `${zahl(v.gewichtG)} g`}
                  </td>
                )}
                {bedarf.masse && <td className="py-1 pr-3 tabular-nums">{masse(v)}</td>}
                {bedarf.eigenschaften && (
                  <td className="py-1 pr-3 text-xs">
                    {v.eigenschaften.length === 0
                      ? '–'
                      : v.eigenschaften.map(e => e.name).join(', ')}
                  </td>
                )}
                {bedarf.tags && (
                  <td className="py-1 pr-3 text-xs">
                    {v.tags.length === 0 ? '–' : v.tags.map(t => t.name).join(', ')}
                  </td>
                )}
                {bedarf.bild && (
                  <td className="py-1 pr-3 text-xs">
                    {v.bilder.length === 0
                      ? <span className="text-[#1A1A1A]/40">–</span>
                      : v.bilder.map(b => (
                        <span key={b.datei} className="mr-2 inline-block">
                          {b.datei}
                          {b.hauptbild && (
                            <span className="ml-1 text-[#1A1A1A]/40" title="Hauptbild">★</span>
                          )}
                        </span>
                      ))}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

/**
 * Farbe oder Größe — mit dem Befund, ob Plenty den Wert kennt.
 *
 * Der rote Punkt ist die wichtigste Information der ganzen Tabelle: ohne den Wert
 * entsteht die Variante ohne dieses Attribut.
 */
function Attributzelle(
  { variante, rolle }: { variante: SchreibbildVariante; rolle: 'farbe' | 'groesse' },
) {
  const attribut = variante.attribute.find(a => a.rolle === rolle);
  if (!attribut) return <td className="py-1 pr-3 text-[#1A1A1A]/40">–</td>;

  return (
    <td className="py-1 pr-3">
      {attribut.wert}
      {attribut.valueId == null
        ? (
          <span
            className="ml-1 text-red-700"
            title={`„${attribut.wert}" gibt es in Attribut ${attribut.attributId} nicht — `
              + 'die Variante bekommt diesen Wert nicht.'}
          >
            ✗
          </span>
        )
        : (
          <span className="ml-1 text-green-700" title={`Plenty-Wert ${attribut.valueId}`}>
            ✓
          </span>
        )}
    </td>
  );
}

/** Der Notnagel ohne Plenty: nur, was in der Datei steht. */
function DateiTabelle({ artikel }: { artikel: VorschauArtikel }) {
  const preisarten = artikel.zeilen.length > 0 ? Object.keys(artikel.zeilen[0].preise) : [];

  return (
    <table className="w-full min-w-[760px] border-collapse text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-[#1A1A1A]/50">
          <th className="py-1 pr-3 font-medium">Zeile</th>
          <th className="py-1 pr-3 font-medium">Nummer</th>
          <th className="py-1 pr-3 font-medium">Farbe</th>
          <th className="py-1 pr-3 font-medium">Größe</th>
          <th className="py-1 pr-3 font-medium">EAN</th>
          <th className="py-1 pr-3 text-right font-medium">Datei-EK</th>
          {preisarten.map(art => (
            <th key={art} className="py-1 pr-3 text-right font-medium">{art}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {artikel.zeilen.map((zeile, i) => (
          <tr
            key={`${zeile.quellZeile}-${i}`}
            className={i === artikel.hauptvariante ? 'font-medium' : ''}
          >
            <td className="py-1 pr-3 tabular-nums text-[#1A1A1A]/60">
              {zeile.quellZeile}
              {i === artikel.hauptvariante && (
                <span className="ml-1 text-[#1A1A1A]/40" title="Hauptvariante">★</span>
              )}
            </td>
            <td className="py-1 pr-3 tabular-nums">{zeile.nummer || '–'}</td>
            <td className="py-1 pr-3">{zeile.farbe || '–'}</td>
            <td className="py-1 pr-3">{zeile.groesse || '–'}</td>
            <td className="py-1 pr-3 tabular-nums">
              {zeile.ean ?? <span className="text-red-700">fehlt</span>}
            </td>
            <td className="py-1 pr-3 text-right tabular-nums">{euro(zeile.importEk)}</td>
            {preisarten.map(art => (
              <td key={art} className="py-1 pr-3 text-right tabular-nums">
                {zeile.preise[art] == null
                  ? <span className="text-[#1A1A1A]/40">–</span>
                  : euro(zeile.preise[art])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
